import { StopTypeEnum } from '@chaine/types'

import {
  BooleanOperatorEnum,
  ConditionTypeEnum,
  DateOperatorEnum,
  IsBeforeAfterNotModifiersEnum,
  SingleSelectOperatorEnum,
  StringOperatorEnum
} from '../types'
import { commonFieldOptions } from './common-field-options'
import { TFieldOptions } from './field-options'
/**
 * Represents all date fields available for autopilot rules for a shipment
 */
export const stopSpecificFieldOptions: TFieldOptions = {
  'appointment.end': {
    defaultConditionsConfig: {
      comparison: {
        comparisonIsAnotherField: false,
        modifier: IsBeforeAfterNotModifiersEnum.TODAY,
        operator: DateOperatorEnum.IS
      },
      conditionName: 'appointment.end',
      conditionType: ConditionTypeEnum.Date
    },
    fieldType: ConditionTypeEnum.Date,
    name: 'Appointment Latest',
    value: 'appointment.end'
  },
  'appointment.start': {
    defaultConditionsConfig: {
      comparison: {
        comparisonIsAnotherField: true,
        modifier: IsBeforeAfterNotModifiersEnum.TODAY,
        operator: DateOperatorEnum.IS
      },
      conditionName: 'appointment.start',
      conditionType: ConditionTypeEnum.Date
    },
    fieldType: ConditionTypeEnum.Date,
    name: 'Appointment Earliest',
    value: 'appointment.start'
  },
  arrivalDateTime: {
    defaultConditionsConfig: {
      comparison: {
        comparisonIsAnotherField: true,
        modifier: IsBeforeAfterNotModifiersEnum.TODAY,
        operator: DateOperatorEnum.IS
      },
      conditionName: 'arrivalDateTime',
      conditionType: ConditionTypeEnum.Date
    },
    fieldType: ConditionTypeEnum.Date,
    name: 'Arrival',
    value: 'arrivalDateTime'
  },
  departureDateTime: {
    defaultConditionsConfig: {
      comparison: {
        comparisonIsAnotherField: true,
        modifier: IsBeforeAfterNotModifiersEnum.TODAY,
        operator: DateOperatorEnum.IS
      },
      conditionName: 'departureDateTime',
      conditionType: ConditionTypeEnum.Date
    },
    fieldType: ConditionTypeEnum.Date,
    name: 'Departure',
    value: 'departureDateTime'
  },
  eta: {
    defaultConditionsConfig: {
      comparison: {
        comparisonIsAnotherField: false,
        modifier: IsBeforeAfterNotModifiersEnum.TODAY,
        operator: DateOperatorEnum.IS
      },
      conditionName: 'eta',
      conditionType: ConditionTypeEnum.Date
    },
    fieldType: ConditionTypeEnum.Date,
    name: 'ETA',
    value: 'eta'
  },
  isDestination: {
    defaultConditionsConfig: {
      comparison: {
        operator: BooleanOperatorEnum.TRUE
      },
      conditionName: 'isDestination',
      conditionType: ConditionTypeEnum.Boolean
    },
    fieldType: ConditionTypeEnum.Boolean,
    name: 'Is destination',
    options: [
      { label: 'True', value: 'true' },
      { label: 'False', value: 'false' }
    ],
    value: 'isDestination'
  },
  isOrigin: {
    defaultConditionsConfig: {
      comparison: {
        operator: BooleanOperatorEnum.TRUE
      },
      conditionName: 'isOrigin',
      conditionType: ConditionTypeEnum.Boolean
    },
    fieldType: ConditionTypeEnum.Boolean,
    name: 'Is origin',
    options: [
      { label: 'True', value: 'true' },
      { label: 'False', value: 'false' }
    ],
    value: 'isOrigin'
  },
  pickupDeliveryNumber: {
    defaultConditionsConfig: {
      comparison: {
        operator: StringOperatorEnum.IS
      },
      conditionName: 'pickupDeliveryNumber',
      conditionType: ConditionTypeEnum.String
    },
    fieldType: ConditionTypeEnum.String,
    name: 'Pickup delivery number',
    value: 'pickupDeliveryNumber'
  },
  type: {
    defaultConditionsConfig: {
      comparison: {
        expectedValue: StopTypeEnum.Pick,
        operator: SingleSelectOperatorEnum.IS
      },
      conditionName: 'type',
      conditionType: ConditionTypeEnum.SingleSelect
    },
    fieldType: ConditionTypeEnum.SingleSelect,
    name: 'Stop type',
    options: [
      {
        label: StopTypeEnum.Pick,
        value: StopTypeEnum.Pick
      },
      {
        label: StopTypeEnum.Drop,
        value: StopTypeEnum.Drop
      }
    ],
    value: 'type'
  },
  'warehouse.name': {
    defaultConditionsConfig: {
      comparison: {
        operator: StringOperatorEnum.CONTAINS
      },
      conditionName: 'warehouse.name',
      conditionType: ConditionTypeEnum.String
    },
    fieldType: ConditionTypeEnum.String,
    name: 'Warehouse Name',
    value: 'warehouse.name'
  }
}

/**
 * Transforms field options by adding a 'parent' prefix to object names, conditionNames, and values
 * @param fieldOptions The original field options object
 * @returns Transformed field options with 'parent' prefix
 */
export const getParentFieldOptions = (fieldOptions: TFieldOptions): TFieldOptions => {
  // First add prefix to all fields
  const prefixedOptions = Object.entries(fieldOptions).reduce((acc, [key, value]) => {
    const newKey = `parent.${key}`
    const transformedValue = {
      ...value,
      defaultConditionsConfig: {
        ...value.defaultConditionsConfig,
        conditionName: `parent.${value.defaultConditionsConfig.conditionName}`
      },
      value: `parent.${value.value}`
    }
    return {
      ...acc,
      [newKey]: transformedValue
    }
  }, {} as TFieldOptions)

  // Then filter to only return fields with parent prefix
  return Object.entries(prefixedOptions).reduce((acc, [key, value]) => {
    if (key.startsWith('parent.')) {
      return {
        ...acc,
        [key]: value
      }
    }
    return acc
  }, {} as TFieldOptions)
}

export const stopFieldOptions: TFieldOptions = {
  ...getParentFieldOptions(commonFieldOptions),
  ...stopSpecificFieldOptions
}

export const stopDateFieldOptions = Object.entries(stopSpecificFieldOptions)
  .filter(([, value]) => value.fieldType === ConditionTypeEnum.Date)
  .map(([, value]) => value)
  .sort((a, b) => a.name.localeCompare(b.name))
